
import { useState, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { useStudents } from "@/contexts"; 
import ActionButtons from "@/components/reports/ActionButtons";
import FilterButtons from "@/components/reports/FilterButtons";
import ProgramDistribution from "@/components/reports/ProgramDistribution";
import MonthlyEnrollments from "@/components/reports/MonthlyEnrollments";
import FeeDistribution from "@/components/reports/FeeDistribution";
import EmptyState from "@/components/reports/EmptyState";
import LoadingState from "@/components/reports/LoadingState";
import ReportsPageSelectionDialog from "@/components/reports/ReportsPageSelectionDialog";
import FormatSelectionDialog from "@/components/shared/FormatSelectionDialog";
import { useReportsData, FilterPeriod } from "@/hooks/useReportsData";
import { StudentData } from "@/contexts/student/types";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Download, Printer, Database, Plus } from "lucide-react";
import { printReportChart, printAllReports } from "@/utils/printing/reportsPrinter";
import { printReportDataPaginatedAsJPG, printReportDataPaginatedAsPDF } from "@/utils/printing/reportsPaginatedPrinter";
import { reportsService } from "@/services/db/reports";
import { toast } from "sonner";

const RECORDS_PER_PAGE = 12; // Set to 12 as requested

const Reports = () => {
  const navigate = useNavigate();
  const { students, loading } = useStudents();
  const [filterPeriod, setFilterPeriod] = useState<FilterPeriod>("all");
  const [savedReports, setSavedReports] = useState<any[]>([]);
  const [isGeneratingReport, setIsGeneratingReport] = useState(false);
  const [isPrinting, setIsPrinting] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  
  // Dialog states for "Print All" functionality
  const [isPrintAllPageDialogOpen, setIsPrintAllPageDialogOpen] = useState(false);
  const [isPrintAllFormatDialogOpen, setIsPrintAllFormatDialogOpen] = useState(false);
  const [printAllSelectedPages, setPrintAllSelectedPages] = useState<{ start: number; end: number } | null>(null);
  
  const { programDistribution, monthlyEnrollments, feeDistribution } = useReportsData(
    students as StudentData[], 
    filterPeriod
  );

  // Calculate total records for all reports combined
  const totalAllReportsRecords = programDistribution.length + monthlyEnrollments.length + feeDistribution.length;

  useEffect(() => {
    loadSavedReports();
  }, []);

  const loadSavedReports = async () => {
    try {
      console.log('📊 Loading saved reports from localStorage only...');
      const reports = await reportsService.getAllReports();
      setSavedReports(reports);
      console.log(`✅ Loaded ${reports.length} saved reports`);
    } catch (error) {
      console.error('Error loading saved reports:', error);
    }
  };

  const handleExportAll = async () => {
    if (isExporting) return;
    
    setIsExporting(true);
    toast.loading("Exporting all reports...", { id: "export-all" });
    
    try {
      const success = await printAllReports(
        programDistribution,
        monthlyEnrollments,
        feeDistribution,
        { format: 'xlsx' }
      );
      
      if (success) {
        toast.success("All reports exported successfully!", { id: "export-all" });
      } else {
        throw new Error("Export failed");
      }
    } catch (error) {
      console.error('Export error:', error);
      toast.error("Failed to export reports", { id: "export-all" });
    } finally {
      setIsExporting(false);
    }
  };

  const handlePrintAll = () => {
    if (!totalAllReportsRecords) {
      toast.error("No report data available to download");
      return;
    }

    // Always show page selection dialog first, regardless of page count
    setIsPrintAllPageDialogOpen(true);
  };

  const handlePrintAllPageSelection = (startPage: number, endPage: number) => {
    setPrintAllSelectedPages({ start: startPage, end: endPage });
    setIsPrintAllPageDialogOpen(false);
    setIsPrintAllFormatDialogOpen(true);
  };

  const handlePrintAllFormatDownload = async (downloadFormat: 'jpg' | 'pdf') => {
    if (isPrinting) return;
    
    setIsPrinting(true);
    
    const loadingToastId = toast.loading(`Generating All Reports Combined ${downloadFormat.toUpperCase()}...`);
    
    try {
      const startPage = printAllSelectedPages?.start || 1;
      const endPage = printAllSelectedPages?.end || Math.ceil(totalAllReportsRecords / RECORDS_PER_PAGE);
      
      // Create combined data array for pagination
      const combinedData = [
        ...programDistribution.map(item => ({ ...item, category: 'Program Distribution' })),
        ...monthlyEnrollments.map(item => ({ ...item, category: 'Monthly Enrollments' })),
        ...feeDistribution.map(item => ({ ...item, category: 'Fee Distribution' }))
      ];
      
      if (downloadFormat === 'pdf') {
        await printReportDataPaginatedAsPDF(combinedData, "All Reports Combined", startPage, endPage);
      } else {
        await printReportDataPaginatedAsJPG(combinedData, "All Reports Combined", startPage, endPage);
      }
      
      toast.dismiss(loadingToastId);
      const pageText = startPage === endPage ? `page ${startPage}` : `pages ${startPage}-${endPage}`;
      toast.success(`All Reports Combined ${pageText} downloaded as ${downloadFormat.toUpperCase()}!`, {
        duration: 2000
      });
      
    } catch (error) {
      console.error('Error downloading all reports:', error);
      toast.dismiss(loadingToastId);
      toast.error("Failed to download all reports");
    } finally {
      setIsPrinting(false);
      setIsPrintAllFormatDialogOpen(false);
      setPrintAllSelectedPages(null);
    }
  };

  const handleGenerateAnalyticsReport = async () => {
    setIsGeneratingReport(true);
    toast.loading("Generating analytics report...", { id: "generate-report" });
    
    try {
      console.log('📊 Generating analytics report with localStorage storage...');
      const dateRange = {
        start: new Date(Date.now() - 90 * 24 * 60 * 60 * 1000).toISOString(), // 90 days ago
        end: new Date().toISOString()
      };
      
      const report = await reportsService.generateAnalyticsReport('student', dateRange);
      toast.success(`Analytics report generated: ${report.title}`, { id: "generate-report" });
      await loadSavedReports();
    } catch (error) {
      console.error('Error generating analytics report:', error);
      toast.error('Failed to generate analytics report', { id: "generate-report" });
    } finally {
      setIsGeneratingReport(false);
    }
  };

  return (
    <div className="animate-fade-in space-y-6">
      {/* Header Section */}
      <div className="space-y-2 mb-6">
        <h2 className="text-2xl font-bold">Reports & Analytics</h2>
        <p className="text-sm text-muted-foreground">Analytics dashboard with localStorage-only storage (5000+ capacity)</p>
        
        {/* Buttons Row */}
        <div className="flex flex-wrap items-center gap-2 mt-4">
          <Button 
            variant="outline" 
            size="xxxsmall"
            onClick={handlePrintAll}
            disabled={loading || totalAllReportsRecords === 0 || isPrinting}
            className="whitespace-nowrap"
          >
            <Printer className="h-3 w-3 mr-1" />
            {isPrinting ? 'Processing...' : 'Print All'}
          </Button>
          <Button 
            variant="outline" 
            size="xxxsmall"
            onClick={handleExportAll}
            disabled={loading || students.length === 0 || isExporting}
            className="whitespace-nowrap"
          >
            <Download className="h-3 w-3 mr-1" />
            {isExporting ? 'Exporting...' : 'Export All'}
          </Button>
          <Button 
            variant="outline" 
            size="xxxsmall"
            onClick={handleGenerateAnalyticsReport}
            disabled={isGeneratingReport}
            className="whitespace-nowrap"
          >
            <Plus className="h-3 w-3 mr-1" />
            {isGeneratingReport ? 'Generating...' : 'Generate Report'}
          </Button>
          <Button 
            variant="outline" 
            size="xxxsmall"
            onClick={() => navigate("/")}
            className="whitespace-nowrap"
          >
            <ArrowLeft className="h-3 w-3 mr-1" />
            Back to Dashboard
          </Button>
        </div>
      </div>

      {/* Saved Reports Section */}
      {savedReports.length > 0 && (
        <Card className="p-4">
          <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
            <Database className="h-5 w-5" />
            Saved Reports ({savedReports.length}) - localStorage Only
          </h3>
          <div className="grid gap-2 md:grid-cols-2 lg:grid-cols-3">
            {savedReports.slice(0, 6).map((report) => (
              <div key={report.id} className="p-3 border rounded-lg">
                <h4 className="font-medium text-sm">{report.title}</h4>
                <p className="text-xs text-muted-foreground">{report.description}</p>
                <p className="text-xs text-muted-foreground">
                  {new Date(report.generatedAt).toLocaleDateString()}
                </p>
              </div>
            ))}
          </div>
        </Card>
      )}

      <div className="mb-8">
        <FilterButtons filterPeriod={filterPeriod} setFilterPeriod={setFilterPeriod} />
      </div>

      {/* Content Section */}
      {loading ? (
        <LoadingState />
      ) : students.length === 0 ? (
        <EmptyState />
      ) : (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          <ProgramDistribution data={programDistribution} />
          <MonthlyEnrollments data={monthlyEnrollments} />
          <FeeDistribution data={feeDistribution} />
        </div>
      )}

      {/* Print All Dialogs */}
      <ReportsPageSelectionDialog
        isOpen={isPrintAllPageDialogOpen}
        onClose={() => setIsPrintAllPageDialogOpen(false)}
        totalRecords={totalAllReportsRecords}
        recordsPerPage={RECORDS_PER_PAGE}
        onDownload={handlePrintAllPageSelection}
        reportTitle="All Reports Combined"
      />

      <FormatSelectionDialog
        isOpen={isPrintAllFormatDialogOpen}
        onClose={() => {
          setIsPrintAllFormatDialogOpen(false);
          setPrintAllSelectedPages(null);
        }}
        onDownload={handlePrintAllFormatDownload}
        totalRecords={totalAllReportsRecords}
        reportTitle="All Reports Combined"
      />
    </div>
  );
};

export default Reports;
